import { findClosestMatchFromObject } from '@/lib/search-utils';
import { createContext, useMemo, useState } from 'react';
import * as z from 'zod';
import { STAGES, useUpload } from '../../csv-importer/use-upload';
import { useAllRotations, useCreateRotationsTimeSlots } from 'shared/hooks/rotations.hooks';
import Checkbox from '../../checkbox';
import { useOrganizationContext } from '../../../[id]/organization.context';
import { validDate } from '@/lib/schema.utils';

export const UploadContext = createContext();

const propertyOptions = {
  startDate: 'Start',
  endDate: 'End',
  email: 'Email',
  code: 'Rotation/Code',
};

export const UploadRotationSlotsProvider = ({ children }) => {
  const { data: rotations = [] } = useAllRotations();
  const createTimeSlots = useCreateRotationsTimeSlots();

  const timeSlotsOptions = useMemo(() => {
    const rotationLabels = rotations.reduce((obj, { name, code, codeAliases }) => {
      [code, ...(codeAliases || [])].forEach(c => {
        obj[c] = name;
      });
      return obj;
    }, {});

    const aliasToCodeMap = rotations.reduce((acc, rotation) => {
      rotation.codeAliases?.forEach(alias => acc.set(alias, rotation.code));
      return acc;
    }, new Map());

    return {
      code: {
        options: rotations.map(({ name, code, codeAliases }) => ({
          key: code,
          value: `${name} (${code}) ${codeAliases?.length ? `[${codeAliases.join(', ')}]` : ''}`,
        })),
        findMatch: target => {
          const res = findClosestMatchFromObject(rotationLabels, { keys: ['key', 'value'] })(
            target,
          );
          return aliasToCodeMap.has(res) ? aliasToCodeMap.get(res) : res;
        },
      },
    };
  }, [rotations]);

  const { organization } = useOrganizationContext();
  const { timezone } = organization;

  const validationSchema = useMemo(
    () =>
      z.object({
        rotations: z.array(
          z
            .object({
              startDate: z.string().transform(validDate(timezone, 'M/d/yyyy')),
              endDate: z.string().transform(validDate(timezone, 'M/d/yyyy')),
              email: z.string().email('Invalid email format'),
              code: z.string({ message: 'Rotation/Code is required' }),
            })
            .refine(data => data.startDate <= data.endDate, {
              message: 'End date must be on or after start date',
              path: ['endDate'],
            }),
        ),
      }),
    [timezone],
  );

  const { showResults, stage, ...rest } = useUpload({
    propertiesMap: timeSlotsOptions,
  });

  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [createUserIfNotExits, setCreateUserIfNotExits] = useState(false);

  const onSubmit = async data => {
    try {
      const { successful, failed } = await createTimeSlots.mutateAsync({
        rotations: data.rotations,
        skipEmail: !sendWelcomeEmail,
        createUserIfNotExits,
      });

      const results = {
        new: successful
          .filter(item => item.user.created)
          .map(item => ({
            email: item.user.email,
            message: 'New user created and successfully set his rotations',
          })),
        updated: successful
          .filter(item => !item.user.created)
          .map(item => ({
            email: item.user.email,
            message: 'User rotations updated successfully',
          })),
        failed: failed.map(error => {
          const emailMatch = error.match(/\(([^)]+)\)/);
          return {
            email: emailMatch?.[1] ?? '',
            message: emailMatch ? error.replace(/\(([^)]+)\)/, '').trim() : error,
          };
        }),
      };
      showResults(results);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <UploadContext.Provider
      value={{
        ...rest,
        stage,
        validationSchema,
        propertyOptions,
        onSubmit,
        mandatoryFields: ['startDate', 'endDate', 'email', 'code'],
        CustomFileUploadRender: () => (
          <div className="mb-4">
            <label className="text-sm text-gray-500">
              Upload a CSV file with the following headers:
            </label>
            <ul className="list-disc pl-5 text-sm text-gray-700">
              <li>Start (format: M/d/yyyy, e.g. 1/25/2025)</li>
              <li>End (format: M/d/yyyy, e.g. 2/28/2025)</li>
              <li>Email (must be a valid email address)</li>
              <li>Rotation/Code (must match an existing rotation code or alias)</li>
            </ul>
          </div>
        ),
        CustomPropsRender: (
          <>
            {stage === STAGES.VALIDATE && (
              <>
                <Checkbox
                  onChange={() => setCreateUserIfNotExits(!createUserIfNotExits)}
                  value={createUserIfNotExits}
                  label={'Create user if not exists'}
                />
                {createUserIfNotExits && (
                  <Checkbox
                    onChange={() => setSendWelcomeEmail(!sendWelcomeEmail)}
                    value={sendWelcomeEmail}
                    label={'Send welcome email'}
                  />
                )}
              </>
            )}
          </>
        ),
      }}>
      {children}
    </UploadContext.Provider>
  );
};
